import FlatList from 'flatlist-react';
import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect, useDispatch } from 'react-redux';
import { useDrag, useDrop , useDragLayer  } from 'react-dnd';
import { useNavigate } from "react-router-dom";
import { Switch, FormControlLabel, Typography } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import COLOR from 'utils/constants/color';
import ClassCard from "components/ClassCard";
import ClassForm from 'features/forms/Class';
import SentryWrap from 'components/Sentry';
import Tag from 'components/Tag';
import styled from 'styled-components';
import { Col, Drawer, Row } from 'antd';
import {  filter , find, flatMap, map } from 'lodash';
import { groupLinkedSection } from 'utils/lib/ebookHelper';
import { handleListUserByClassForTeacher, handleListUserClasses, handleUpdateClasseOrder } from 'store/userClasses/thunks';
import { handleSyncSchoologyClasses, handleSyncSchoologyClassesUsers } from 'store/schoology/thunks';
import { handlesynClassUsers, handleUserClassesSync } from 'store/classlink/thunks';
import { handeleCanvasClassUsers } from 'store/canvas/thunks';
import { handleCleverClassesUserSync } from 'store/clever/thunks';
import { handleGoogleClassRoomStudents } from 'store/googleClassRoom/thunks';
import { captureException } from '@sentry/react';
import SpinLoader from 'components/Loader/SpinLoader';
import { CourseSync } from './CourseSync';
import starSvg from "../utils/assets/images/star.svg";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
  } from "@dnd-kit/core";
  import {
    arrayMove,
    SortableContext,
    useSortable,
    rectSortingStrategy,
  } from "@dnd-kit/sortable";
  import { CSS } from "@dnd-kit/utilities";


//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------

const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// MUI styled components
const StyledLabel = muiStyled(Typography)({
    fontSize: '18px',
    color: '#582f81',
});

const StyledFormControlLabel = muiStyled(FormControlLabel)({
    margin: '24px',
    marginTop: '12px',
});

// Styled-components for layout and basic elements
const CenterContainer = styled.div`
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
`;

const ButtonClear = styled.button`
    border-radius: 7px;
    color: #fff;
    border: none;
    cursor: pointer;
    outline: none;
    padding: 10px;
    margin: 10px;
`;

const Note = styled.span`
    color: ${COLOR.black400};
    display: block;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    margin-bottom: 16px;
`;

const ButtonClass = styled.button`
    border-radius: 25.5px;
    background: #582F81;
    min-height: 41px;
    min-width: 200px;
    padding: 5px 5px;
    color: #ffffff;
    font-size: 18px;
    border: none;
    outline: none;
    &:hover {
        background: #582f81e3 !important;
    }
`;

const SectionTitle = styled.div`
    background: ${COLOR.tabActive};
    letter-spacing: 0.5px;
`;

const OuterWrapper = styled.div`
    @media only screen and (max-width: 576px) {
        margin: 0px 12px;
        width: calc(100% - 24px);
    }
    @media only screen and (min-width: 576px) and (max-width: 992px) {
        margin: 0px 12px;
        width: calc(50% - 24px);
    }
    @media only screen and (min-width: 992px) and (max-width: 1200px) {
        margin: 0px 12px;
        width: calc(33.33% - 24px);
    }
    @media only screen and (min-width: 1200px) {
        margin: 0px 12px;
        width: calc(25% - 24px);
    }
`;

const AnimatedArrowContainer = styled.div`
    margin: 0 auto;
    text-align: center;
    width: 100px;
    height: 100px;
    position: relative;
    cursor: pointer;
    filter: url("#goo");
`;

const AnimatedArrowWrapper = styled.div`
    display: flex;
    justify-content: center;
    gap: 20px;
`;

const ArrowCircle = styled.div`
    background: #582f81;
    border-radius: 50%;
    display: inline-block;
    height: 100px;
    width: 100px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
    transform-origin: 0%;
`;

const ArrowCircleTop = styled(ArrowCircle)`
    height: 33.33px;
    width: 41.67px;
    animation: blob-1-anim 3s cubic-bezier(0.77, 0, 0.175, 1) infinite;
    z-index: 0;
    top: 0;
`;

const ArrowCircleBottom = styled(ArrowCircle)`
    height: 33.33px;
    width: 41.67px;
    animation: blob-2-anim 3s cubic-bezier(0.77, 0, 0.175, 1) infinite;
    z-index: 9;
    bottom: 0px;
`;

const ArrowSvg = styled.svg`
    z-index: 9;
    position: relative;
    background: #582f81;
    border-radius: 50%;
    display: inline-block;
    height: 100px;
    width: 100px;

    .st-arrow {
        fill: #fff;
        animation: scrolly 3s cubic-bezier(0.77, 0, 0.175, 1) forwards infinite;
        perspective: 9000px;
        transform: translateZ(0);
        transform-origin: bottom;
    }
`;

const AnimatedArrowBottom = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    transform: ${props => props.isAtBottom ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0s ease;
`;

function ToggleClasses({ showHiddenClasses, setShowHiddenClasses }) {
    const handleToggle = (event) => {
        setShowHiddenClasses(event.target.checked);
    };

    return (
        <StyledFormControlLabel
            control={
                <Switch
                    checked={showHiddenClasses}
                    onChange={handleToggle}
                    color="primary"
                />
            }
            label={<StyledLabel>{showHiddenClasses ? "Archived classes " : "Active classes"}</StyledLabel>}
            labelPlacement="start"
        />
    );
}

const ItemType = 'CARD';

const layerStyles = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 100,
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
};

function getItemStyles(initialOffset, currentOffset) {
    if (!initialOffset || !currentOffset) {
        return {
            display: 'none',
        };
    }

    const { x, y } = currentOffset;
    const transform = `translate(${x}px, ${y}px)`;
    return {
        transform,
        WebkitTransform: transform,
        opacity: 0.9, // Set desired opacity here
        maxWidth:400
    };
}

const CustomDragLayer = ({all_items}) => {
    const {
        itemType,
        isDragging,
        item,
        initialOffset,
        currentOffset,
    } = useDragLayer((monitor) => ({
        item: monitor.getItem(),
        itemType: monitor.getItemType(),
        initialOffset: monitor.getInitialSourceClientOffset(),
        currentOffset: monitor.getSourceClientOffset(),
        isDragging: monitor.isDragging(),
    }));
    const item_data = useMemo(()=>find(all_items,['id',item?.id]) || item , [all_items,item])
    if (!isDragging) {
        return null;
    }

    return (
        <div style={layerStyles}>
            <div style={getItemStyles(initialOffset, currentOffset)}>
                <ClassCard key={item_data.id} classInfo={item_data} />
            </div>
        </div>
    );
};

const AnimatedArrow = ({ onClick, direction }) => {
    const isUp = direction === 'up';

    return (
        <AnimatedArrowContainer onClick={onClick} className={`animated-arrow ${!isUp ? "up" : ""}`}>
            <AnimatedArrowBottom isAtBottom={isUp}>
                <ArrowCircleTop />
                <ArrowCircle className="circle-top" />
                <ArrowCircle className="circle-main" />
                <ArrowCircle className="circle-bottom" />
                <ArrowCircleBottom />
                <ArrowSvg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    x="0px"
                    y="0px"
                    viewBox="0 0 612 612"
                    style={{ enableBackground: "new 0 0 612 612" }}
                    xmlSpace="preserve"
                >
                    {/* same defs and filter */}
                    <path
                        className="st-arrow"
                        d="M317.5,487.6c0.3-0.3,0.4-0.7,0.7-1.1l112.6-112.6c6.3-6.3,6.3-16.5,0-22.7c-6.3-6.3-16.5-6.3-22.7,0
                        l-86,86V136.1c0-8.9-7.3-16.2-16.2-16.2c-8.9,0-16.2,7.3-16.2,16.2v301.1l-86-86c-6.3-6.3-16.5-6.3-22.7,0
                        c-6.3,6.3-6.3,16.5,0,22.7l112.7,112.7c0.3,0.3,0.4,0.7,0.7,1c0.5,0.5,1.2,0.5,1.7,0.9c1.7,1.4,3.6,2.3,5.6,2.9
                        c0.8,0.2,1.5,0.4,2.3,0.4C308.8,492.6,313.8,491.3,317.5,487.6z"
                    />
                </ArrowSvg>
                <style jsx>{`
                .animated-arrow {
                    text-align: center;
                    width: 100px;
                    height: 100px;
                    position: relative;
                    cursor: pointer;
                    filter: url("#goo");
                }

                .animated-arrow.down {
                    transform: rotate(180deg);
                }

                .arrow-body {
                    position: absolute;
                    left: 50%;
                    top: 10px;
                    animation: blob-1-anim 2s infinite ease-in-out;
                    transform: translateX(-50%);
                }

                .circle-top,
                .circle-main,
                .circle-bottom {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: #ccc;
                    position: absolute;
                    left: 50%;
                }

                .circle-top {
                    top: 0;
                    animation: blob-2-anim 2s infinite ease-in-out;
                }

                .circle-main {
                    top: 20px;
                }

                .circle-bottom {
                    top: 40px;
                    animation: rotate-circle 4s linear infinite;
                }

                .arrow-svg {
                    width: 40px;
                    height: 40px;
                    position: absolute;
                    top: 60px;
                    left: 50%;
                    transform: translateX(-50%);
                    animation: scrolly 3s infinite ease-in-out;
                }

                .st-arrow {
                    fill: #000;
                }

                @keyframes blob-1-anim {
                    0% {
                        transform: translateX(-50%) translateY(0);
                    }
                    14% {
                        transform: translateX(-50%) translateY(-8px);
                    }
                    24% {
                        transform: translateX(-50%) translateY(0);
                    }
                    100% {
                        transform: translateX(-50%) translateY(0);
                    }
                }

                @keyframes blob-2-anim {
                    0%,
                    30%,
                    70% {
                        transform: scale(1) translate(-50%, 10px);
                    }
                    95% {
                        transform: scale(1) translate(-50%, 26px);
                    }
                    100% {
                        transform: scale(1) translate(-50%, 10px);
                    }
                }

                @keyframes scrolly {
                    0% {
                        transform: translate3d(0, -150%, 0) rotateX(90deg) scale(0.5)
                        skewX(3deg);
                    }
                    30%,
                    70% {
                        transform: translate3d(0, 0, 0) rotateX(0deg) scale(1) skewX(0deg);
                    }
                    95%,
                    100% {
                        transform: translate3d(0, 50%, 0) rotateX(-90deg) scale(0.5)
                        skewX(-3deg);
                    }
                }

                @keyframes rotate-circle {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(180deg);
                    }
                }
            `}</style>
            </AnimatedArrowBottom>
        </AnimatedArrowContainer>
    );
};

async function synClassUsers(classes, currentUser, integration_type) {
    const syncHandlers = {
        schoology: (classId) => handleSyncSchoologyClassesUsers(currentUser.schoologyid, classId),
        canvas: (classId) => handeleCanvasClassUsers(currentUser.canvas_id, currentUser.canvas_auth_id, classId),
        classlink: (classId) => handlesynClassUsers(currentUser.classlinkid, currentUser.classlinkTenantId, classId),
        clever: (classId) => handleCleverClassesUserSync(currentUser.cleverId, classId),
        google: (classId) => handleGoogleClassRoomStudents(currentUser.googleId, classId),
    };
    const syncHandler = syncHandlers[integration_type];
    if (!syncHandler) {
        console.warn(`Unknown integration type: ${integration_type}`);
        return;
    }
    try {
        await Promise.allSettled(classes.map(c => syncHandler(c.id)));
    } catch (error) {
        if (error?.message !== "Network Error") {
            captureException(`839 Error syncing class users: ${error}`);
        }
    }
}

// Sortable card component for DndContext
function SortableClassCard({ classInfo, isUserFromOtherPlatform }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: classInfo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <OuterWrapper 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
      className="list__item js-item"
      aria-label={`Drag ${String(classInfo?.id)}`}
    >
      <ClassCard classInfo={{ ...classInfo, isUserFromOtherPlatform }} />
    </OuterWrapper>
  );
}

function DragReorderList({ items, onReorder, moveCard, isUserFromOtherPlatform }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Safety check: ensure both active and over items exist
    if (!active || !over || !active.id || !over.id) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);
      
      // Safety check: ensure indices are valid
      if (oldIndex === -1 || newIndex === -1) {
        console.warn('Invalid drag operation: could not find item indices');
        return;
      }
      
      // Call the existing moveCard function which now handles backend updates
      moveCard(oldIndex, newIndex);
      
      // Note: onReorder is no longer called here since moveCard handles the state update
      // and backend synchronization
    }
  };

  // Safety check to ensure items have valid IDs
  const validItems = items.filter(item => item && item.id);
  
  if (validItems.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      id="drag-reorder-list-dnd"
    >
      <SortableContext items={validItems.map(item => item.id)} strategy={rectSortingStrategy}>
        <div style={{ display: "flex", rowGap: "50px", flexWrap: "wrap", width: "100%" }}>
          {validItems.map((label) => (
            <SortableClassCard 
              key={label.id} 
              classInfo={label} 
              isUserFromOtherPlatform={isUserFromOtherPlatform}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
    );
}

function CrossedCoursesDragList({ items, onReorder, renderWhenEmpty, isUserFromOtherPlatform }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Safety check: ensure both active and over items exist
    if (!active || !over || !active.id || !over.id) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);
      
      // Safety check: ensure indices are valid
      if (oldIndex === -1 || newIndex === -1) {
        console.warn('Invalid drag operation: could not find item indices');
        return;
      }
      
      // Create new array with reordered items
      const newItems = arrayMove(items, oldIndex, newIndex);
      
      // Call onReorder callback to update parent state
      if (onReorder) {
        onReorder(newItems);
      }
    }
  };

  // Safety check for items
  if (!items || items.length === 0) {
    return renderWhenEmpty ? renderWhenEmpty() : null;
  }

  // Additional safety check to ensure items have valid IDs
  const validItems = items.filter(item => item && item.id);
  if (validItems.length === 0) {
    return renderWhenEmpty ? renderWhenEmpty() : null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      id="crossed-courses-dnd"
    >
      <SortableContext items={validItems.map(item => item.id)} strategy={rectSortingStrategy}>
        <div style={{ display: "flex", rowGap: "50px", flexWrap: "wrap", width: "100%" }}>
          {validItems.map((item) => (
            <SortableClassCard 
              key={item.id} 
              classInfo={item} 
              isUserFromOtherPlatform={isUserFromOtherPlatform}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function LearnScreen({
    currentClass,
    currentOrg,
    currentUser,
    userClasses,
    fromDrawer,
    setVisible = () => { },
    from_menu
}) {
    const [classDrawer, toggleClassDrawer] = useState(false);
    const isSchoologyUser = !!(currentUser && currentUser.schoologyid)
    const isCleverUser = !!(currentUser && currentUser.cleverId);
    const isCanvasUser = !!(currentUser && currentUser.canvas_id);
    const [canScroll, setCanScroll] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const isGoogleUser = !!(currentUser && currentUser.googleId);
    const isClassLinkuser = !!(currentUser && currentUser.classlinkid);
    const isUserFromOtherPlatform =  isSchoologyUser || isCleverUser || isGoogleUser || isClassLinkuser || isCanvasUser
    const [classes,setClasses] = useState(filter(userClasses,k=>!k.archived))
    const [showHiddenClasses,setShowHiddenClasses] = useState(false);
    const navigate = useNavigate()
    const [data, setData] = useState({});
    const dispatch = useDispatch();
    const [showDownArrow, setShowDownArrow] = useState(true);
    const [showUpArrow, setShowUpArrow] = useState(false);
    const [non_crossed_courses,setNonCrossedCourses] = useState([])
    const [archivedClasses,setArchivedClasses] = useState([])
    const crossed_courses = useMemo(()=>filter(classes, k => k.canvas_cross_course_id),[classes]);
    const [crossedCoursesOrder, setCrossedCoursesOrder] = useState([])
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [update_done,setUpdateDone] = useState(0)
    const [linkedSectionOrder, setLinkedSectionOrder] = useState({})
    const is_touch = useMemo(()=>isTouchDevice(),[])
    const [studentSyncLoader,setStudentSyncLoader] = useState(false);
    const sync_class_done = useRef(0);
    const synModalRef = useRef(null);

    useEffect(()=>{
        const groupByLinkSection = groupLinkedSection(classes , isSchoologyUser)
        const flat_groupByLinkSection = flatMap(groupByLinkSection, k => k)
            const non_crossed_courses_item = filter(classes, k =>{
                const is_in_group = find(flat_groupByLinkSection, flt => flt.id == k.id)
            return !k.canvas_cross_course_id && !is_in_group
        })
        setNonCrossedCourses(non_crossed_courses_item.sort((a,b) => a.order - b.order))
    },[classes])

    // Initialize crossed courses order when crossed_courses change
    useEffect(() => {
        if (crossed_courses && crossed_courses.length > 0) {
            setCrossedCoursesOrder(crossed_courses.sort((a, b) => a.order - b.order));
        }
    }, [crossed_courses]);
    const handleSyncUser = async (active_classes, integration_type) => {
        try {
            if(active_classes.length === 0) return;
            setStudentSyncLoader(true);
            if(sync_class_done.current>0) return;
            if(sync_class_done.current === active_classes.length) return;
            sync_class_done.current = active_classes.length;
            await synClassUsers(active_classes, currentUser, integration_type)
            await  dispatch(handleListUserByClassForTeacher(currentUser.id))
        } catch (error) {
           captureException(`838 Error syncing class users: ${error}`);
        } finally {
            setStudentSyncLoader(false);
        }
    }
    useEffect(() => {
        // if(Array.isArray(userClasses) && userClasses.length > 0&&!from_menu) {
        //     const active_classes = filter(userClasses, k => !k.archived);
        //     if (active_classes.length > 0) {
        //         const is_schoology = find(active_classes, k => k.schoolId);
        //         const is_canvas = find(active_classes, k => k.canvas_id);
        //         const is_clever = find(active_classes, k => k.cleverId);
        //         const is_google = find(active_classes, k => k.googleId);
        //         const is_classlink = find(active_classes, k => k.classlinkid);
        //         const is_other_platform = is_schoology || is_canvas || is_clever || is_google || is_classlink;
        //         const integration_type = is_schoology ? "schoology" : is_canvas ? "canvas" : is_clever ? "clever" : is_google ? "google" : is_classlink ? "classlink" : null;
        //         if( is_other_platform) {
        //            handleSyncUser(active_classes, integration_type);
        //         }
        //     }
        // }
    }, [userClasses,currentUser,from_menu]);

   
    const moveCard = (fromIndex, toIndex, sectionKey = null) => {
        if (sectionKey) {
            // Handle linked section items
            const updatedItems = [...(linkedSectionOrder[sectionKey] || linked_classes[sectionKey])];
            const [movedItem] = updatedItems.splice(fromIndex, 1);
            updatedItems.splice(toIndex, 0, movedItem);
            setLinkedSectionOrder(prev => ({
                ...prev,
                [sectionKey]: updatedItems
            }));
            // Update backend immediately for linked sections
            setTimeout(()=>{
                dispatch(handleUpdateClasseOrder(updatedItems.map((a, ind) => ({ id: a.id, order: ind }))))
            },1000)
        } else {
            // Handle non-crossed courses
            const updatedItems = [...non_crossed_courses];
            const [movedItem] = updatedItems.splice(fromIndex, 1);
            updatedItems.splice(toIndex, 0, movedItem);
            setNonCrossedCourses(updatedItems);
            // Update backend immediately for non-crossed courses
            setTimeout(()=>{
                dispatch(handleUpdateClasseOrder(updatedItems.map((a, ind) => ({ id: a.id, order: ind }))))
            },1000)
        }
    };

    const handleCrossedCoursesReorder = (newOrder) => {
        setCrossedCoursesOrder(newOrder);
        // Update the backend immediately for crossed courses
        if (Array.isArray(newOrder)) {
            dispatch(handleUpdateClasseOrder(newOrder.map((a, ind) => ({ id: a.id, order: ind }))))
        }
    };
    const renderItem = (item, index) => {
        return (
            <OuterWrapper> 
                <ClassCard key={item.id} classInfo={item} />
            </OuterWrapper>
        )
    }
    useEffect(()=>{
        onClassDataChange()
    },[userClasses,showHiddenClasses])
    // This useEffect is kept for any remaining edge cases but is no longer the primary update mechanism
    useEffect(() => {
        if (update_done > 0) {
            // Only handle any remaining updates that might not have been handled directly
            console.log('Update done triggered:', update_done);
        }
    }, [update_done])
    useEffect(()=>{
        if(currentUser&&currentUser.classlinkid){
            handleUserClassesSync(currentUser.classlinkid,currentUser.classlinkTenantId)
        }
    },[currentUser])
    useEffect(() => {
        if (currentUser && currentUser?.schoologyid) {
            handleSyncSchoologyClasses(currentUser?.schoologyid)
        }
    }, [currentUser])
    const onClassDataChange = () => {
        setArchivedClasses(filter(userClasses, k => k.archived))
        setClasses(filter(userClasses, k => !k.archived))
    }
    const getClass = async () => {
        if (currentUser && currentUser.id) {
            await dispatch(handleListUserClasses(currentUser.id));
        }
        toggleClassDrawer(false);
    }
    const redirectToHome = () => {
        navigate(`/admin/class?view=home`);
    }
    useEffect(() => {
        if (userClasses && userClasses.length == 0) {
            if (isUserFromOtherPlatform) {
                // redirectToHome()
                if(synModalRef.current){
                    synModalRef.current.openModal()
                }
            } else {
                toggleClassDrawer(true)
            }
        }
    }, [userClasses])
    const archiveClasses = filter(userClasses, k => k.archived);
    const archiveCount = archiveClasses.length > 0 ? `(${archiveClasses.length})` : ""
    const archiveTxt = `Archived Classes ${archiveCount}`;
    const linked_classes = groupLinkedSection(classes , isSchoologyUser)
    const has_link = crossed_courses.length >0 || Object.keys(linked_classes).length>0;

    useEffect(() => {
        const classNames = ["overflow-hidden"];

        if (classDrawer) {
            document.body.classList.add(...classNames);
        } else {
            document.body.classList.remove(...classNames);
        }
        return () => {
            document.body.classList.remove(...classNames);
        };
    }, [classDrawer]);

    useEffect(() => {
        if (!fromDrawer) return;

        setTimeout(() => {
            const popoverContent = document.querySelector(
                ".custom-popover .ant-popover-inner-content"
            );

            if (!popoverContent) return;

            const popoverWrapper = Array.from(popoverContent.children).find(
                (child) => child.scrollHeight > child.clientHeight
            );

            if ((popoverWrapper && popoverWrapper.scrollHeight > popoverWrapper.clientHeight) || classes?.length > 4) {
                setCanScroll(true);
            
                const checkIfAtBottom = () => {
                    if (popoverWrapper) {
                        const { scrollHeight, clientHeight, scrollTop } = popoverWrapper;
                        const scrollPercentage = Math.min(
                            (scrollTop + clientHeight) / scrollHeight,
                            1
                        );
                        setIsAtBottom(scrollPercentage >= 0.98);
                    }
                };
            
                checkIfAtBottom();
            
                if (popoverWrapper) {
                    popoverWrapper.addEventListener('scroll', checkIfAtBottom);
                }
            
                return () => {
                    if (popoverWrapper) {
                        popoverWrapper.removeEventListener('scroll', checkIfAtBottom);
                    }
                };    
            } else {
                setCanScroll(false);
            }
        }, 0);
    }, [fromDrawer, showHiddenClasses, userClasses, classes]);

    const updateArrowVisibility = (wrapper) => {
        const { scrollTop, scrollHeight, clientHeight } = wrapper;
        const isAtTop = scrollTop <= 10;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

        setShowUpArrow(!isAtTop);
        setShowDownArrow(!isAtBottom);
    };

    const handleScroll = (direction = "down") => {
        if (!fromDrawer) return;

        const popoverContent = document.querySelector(
            ".custom-popover .ant-popover-inner-content"
        );
        if (!popoverContent) return;

        const popoverWrapper = Array.from(popoverContent.children).find(
            (child) => child.scrollHeight > child.clientHeight
        );
        if (!popoverWrapper) return;

        const scrollAmount = popoverWrapper.clientHeight * 0.9;
        popoverWrapper.scrollBy({
            top: direction === "down" ? scrollAmount : -scrollAmount,
            behavior: "smooth",
        });

        setTimeout(() => updateArrowVisibility(popoverWrapper), 400);
    };

    useEffect(() => {
        if (!fromDrawer) return;

        const popoverContent = document.querySelector(
            ".custom-popover .ant-popover-inner-content"
        );
        if (!popoverContent) return;

        const popoverWrapper = Array.from(popoverContent.children).find(
            (child) => child.scrollHeight > child.clientHeight
        );
        if (!popoverWrapper) return;

        updateArrowVisibility(popoverWrapper);

        const handleScrollEvent = () => updateArrowVisibility(popoverWrapper);

        popoverWrapper.addEventListener("scroll", handleScrollEvent);

        return () => {
            popoverWrapper.removeEventListener("scroll", handleScrollEvent);
        };
    }, [fromDrawer, showHiddenClasses, userClasses, classes]);


    return (
        <>
        <SpinLoader dataloader={studentSyncLoader}/>
            <Drawer
                height='100%'
                onClose={() => {
                    toggleClassDrawer(false);
                    setData({});
                }}
                placement='top'
                open={classDrawer}
                key='classNew'
                className="addNewDrawer"
            >
                <CenterContainer>
                    <SentryWrap>
                        {classDrawer && <ClassForm
                            data={data}
                            setData={setData}
                            currentOrg={currentOrg}
                            currentUser={currentUser}
                            currentClass={currentClass}
                            userClasses={userClasses}
                            onClosed={(hasClassLoad, classId) => {
                                setData({})
                                getClass(hasClassLoad, classId);
                                toggleClassDrawer(false)
                            }}
                        />}
                    </SentryWrap>
                </CenterContainer>
            </Drawer>

            {fromDrawer && canScroll && (
                <div
                    id="animated-arrow-container"
                    style={{
                        position: "absolute",
                        left: "50%",
                        bottom: "-50px",
                        transform: "translate(-50%) scale(0.5)",
                        zIndex: 1000,
                    }}
                >
                    <AnimatedArrowWrapper>
                        {showDownArrow && (
                            <AnimatedArrow direction="down" onClick={() => handleScroll("down")} />
                        )}
                        {showUpArrow && (
                            <AnimatedArrow direction="up" onClick={() => handleScroll("up")} />
                        )}
                    </AnimatedArrowWrapper>
                </div>
            )}

            <div className='headerCreateClass'>
                <div className="createClassTgls">
                 {!isUserFromOtherPlatform && <ButtonClass onClick={() => { setVisible(false); toggleClassDrawer(true) }}>Create classes</ButtonClass>}
                 <CourseSync ref={synModalRef}/>
                 <ToggleClasses setShowHiddenClasses={setShowHiddenClasses} showHiddenClasses={showHiddenClasses} />
                </div>
                
                {showHiddenClasses ?
                <div className='headerNote'>
                    <Tag bgColor={COLOR.redishpink}><img src={starSvg} alt="" /><strong>NOTE</strong></Tag>
                    <Note style={{ color: COLOR.black100 }}>These are hidden classes.</Note>
                </div> 
                : 
                <div className='headerNote'>
                    <Tag bgColor={COLOR.redishpink}><img src={starSvg} alt="" /><strong>NOTE</strong></Tag>
                    <Note style={{ color: COLOR.black100 }}>You can drag and reorganize your classes as needed.</Note>
                </div>
                }
            </div>
              
              <Row className={`${!fromDrawer && "1111 teacherHm"}`}>
                <Col xs={24} sm={24} md={24} lg={24} className='333 allTechrColectBtn newAllTchrColectBtn'>

                    {!showHiddenClasses&&Object.keys(linked_classes).length > 0 ? <Row gutter={[40, 30]}>
                        {Object.keys(linked_classes).map((key, index) => {
                            const has_more_classes = linked_classes[key]?.length>1
                            const linked_class = linked_classes[key];
                            const sectionItems = (linkedSectionOrder[key] && linkedSectionOrder[key].length
                                ? linkedSectionOrder[key]
                                : [...linked_class].sort((a,b) => a.order - b.order));
                            return <div key={key} className={`teacherHmInr ${has_more_classes?"cross-section-teacher-home":''}`} style={{ display: "flex", rowGap: "50px", flexWrap: "wrap" ,  width:"100%"}}>
                                {has_more_classes ? <SectionTitle className='sectionTitle'>{"Linked section"}</SectionTitle>:null}
                                <>
                                    <Row style={{ display: 'flex', flexWrap: 'wrap', rowGap: '50px', width: "100%" }}>
                                      <DragReorderList
                                        items={sectionItems}
                                        onReorder={(newItems) => setLinkedSectionOrder(prev => ({ ...prev, [key]: newItems }))}
                                        moveCard={(from, to) => moveCard(from, to, key)}
                                        isUserFromOtherPlatform={isUserFromOtherPlatform}
                                      />
                                    </Row>
                                    <CustomDragLayer all_items={sectionItems} />
                                </>
                            </div>
                        })}
                    </Row> : null}


                    {!showHiddenClasses && crossed_courses.length > 0 ? <Row  className={`teacherHmInr ${crossed_courses.length>1?" cross-section-teacher-home":''}`} style={{ flexWrap: "wrap", rowGap: '50px', width:"100%" }}>
                        {crossed_courses.length>1?  <SectionTitle className='sectionTitle'>Cross-listed</SectionTitle>:null}
                        <CrossedCoursesDragList
                            items={crossedCoursesOrder}
                            onReorder={handleCrossedCoursesReorder}
                            renderWhenEmpty={() => <div className='noArchvClasses'>{showHiddenClasses ? `No Archived classes` : `No Class found!`}</div>}
                            isUserFromOtherPlatform={isUserFromOtherPlatform}
                        />
                    </Row> : null}


                    {showHiddenClasses ? <div className="teacherHmInr non-cross-section-teacher-home"  style={{ gap: "50px", flexWrap: "wrap", width: "100%" }}>
                        <Row style={{ display: 'flex', flexWrap: 'wrap', rowGap: '50px', width: "100%" }}>
                            <FlatList
                                list={archivedClasses}
                                renderItem={renderItem}
                                renderWhenEmpty={() => <div className='noArchvClasses'>{`No Archived classes`}</div>}
                                sortBy={["id"]}
                            />
                        </Row>
                    </div> : null}
                    
                    {!showHiddenClasses &&non_crossed_courses.length > 0 ?
                        <div className="teacherHmInr non-cross-section-teacher-home" style={{ marginTop: has_link > 0 ? 50 : 0, padding: has_link > 0 ? 30 : 0, flexWrap: "wrap" }}>
                            <>
                                <Row style={{ display: 'flex', flexWrap: 'wrap', rowGap: '50px', width: "100%" }}>
                                   <DragReorderList items={non_crossed_courses} onReorder={setNonCrossedCourses} moveCard={moveCard} isUserFromOtherPlatform={isUserFromOtherPlatform} />
                                </Row>
                                <CustomDragLayer all_items={non_crossed_courses} />


                                


                            </>
                        </div> : null}
                </Col>
            </Row>
        </>
    );
}

const mapStateToProps = state => ({
    currentClass: state.currentClass,
    currentOrg: state.currentOrg,
    currentUser: state.currentUser,
    userClasses: state.userClasses,
});

export default connect(mapStateToProps, null)(LearnScreen);